import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createContact, getContact, updateContact } from '../../../api/contact';
import { getAgents } from '../../../api/agent';
import { hasAdminAccess } from '../../../utils/roleAccess';
import { User, Mail, Phone, MapPin, Tag, FileText, ArrowLeft, Upload, Download, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiCache } from '../../../lib/cache';
import * as documentsAPI from '../../../api/documents';

// Pull a human-readable message out of a DRF error response so the user sees
// the real reason (e.g. "A contact with this email already exists.") instead of
// a generic toast. DRF returns either {field: ["msg", ...]} or {detail: "msg"}.
const getServerErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    const firstKey = Object.keys(data)[0];
    if (firstKey) {
        const val = data[firstKey];
        const msg = Array.isArray(val) ? val[0] : val;
        if (msg) return firstKey === 'detail' || firstKey === 'non_field_errors' ? msg : `${firstKey}: ${msg}`;
    }
    return fallback;
};

// The form collects First / Last / Preferred name, but `name` is still the
// canonical full name used everywhere else in the CRM, so keep it derived from
// the two legal-name parts.
const buildFullName = (firstName, lastName) =>
    [(firstName || '').trim(), (lastName || '').trim()].filter(Boolean).join(' ');

// Older contacts (and everyone imported from a CSV) only have `name` filled in.
// Split it on the first space so the form opens with the parts populated
// instead of blank boxes that would wipe the name on save.
const splitLegacyName = (contact) => {
    if (contact.first_name || contact.last_name) return contact;
    const parts = (contact.name || '').trim().split(/\s+/);
    if (!parts[0]) return contact;
    return {
        ...contact,
        first_name: parts[0],
        last_name: parts.slice(1).join(' '),
    };
};

// Sources that are assigned automatically by an intake flow (not picked by an
// agent). The edit form surfaces these read-as-an-option so saving a portal
// contact preserves where they signed up instead of blanking it.
const SYSTEM_SOURCE_LABELS = {
    buyer_portal: 'Buyer Portal',
    appraisal_form: 'Appraisal / Sell Form',
    block_form: 'Block of Units Form',
    portal_signup: 'Portal Sign-up',
    enquiry: 'Property Enquiry',
    mortgage_enquiry: 'Mortgage Enquiry',
    pro_tool: 'Pro Tool Interest',
};

const ContactFormComponent = ({ isEdit = false }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);

    // Assigned agent (DOMA 13261735033) — admin-controlled. A regular agent who
    // adds a contact is stamped as the agent server-side; admins pick the owner.
    const { user } = useSelector((state) => state.auth);
    const isAdmin = hasAdminAccess(user);
    const [agents, setAgents] = useState([]);
    useEffect(() => {
        if (!isAdmin) return;
        getAgents({ role: 'agent', page_size: 200 })
            .then((res) => setAgents(res.data?.results || res.data || []))
            .catch(() => setAgents([]));
    }, [isAdmin]);

    const [formData, setFormData] = useState({
        name: '',
        first_name: '',
        last_name: '',
        preferred_name: '',
        company: '',
        position: '',
        email: '',
        phone: '',
        alternate_phone: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'Australia',
        contact_type: 'general',
        source: 'website',
        tags: [],
        notes: '',
        preferred_contact_method: 'email',
        do_not_contact: false,
        email_opt_in: true,
        sms_opt_in: true,
        birthday: ''
    });
    // The name this contact was loaded with. Some legacy/CSV records have a full
    // name that isn't just "first last" (e.g. "John & Jane Smith"); if the agent
    // doesn't touch the name boxes we keep the stored name exactly as it was
    // rather than flattening it on an unrelated edit.
    const [nameBaseline, setNameBaseline] = useState(null);
    const [tagInput, setTagInput] = useState('');
    const [documents, setDocuments] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        if (isEdit && id) {
            loadContact();
            fetchDocuments();
        }
    }, [isEdit, id]);

    const fetchDocuments = async () => {
        if (!id) return;

        try {
            setLoadingDocs(true);
            const response = await documentsAPI.getDocuments({ related_contact: id });
            setDocuments(Array.isArray(response) ? response : response.results || []);
        } catch (error) {
            console.error('Error fetching documents:', error);
            setDocuments([]);
        } finally {
            setLoadingDocs(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUploadDocument = async () => {
        if (!selectedFile) {
            toast.error('Please select a file');
            return;
        }

        if (!id) {
            toast.error('Please save the contact first before uploading documents');
            return;
        }

        setUploading(true);
        try {
            const formDataDoc = new FormData();
            formDataDoc.append('file', selectedFile);
            formDataDoc.append('name', selectedFile.name);
            formDataDoc.append('description', `Document for contact: ${formData.name}`);
            formDataDoc.append('category', 'miscellaneous');
            formDataDoc.append('related_contact', id);

            await documentsAPI.uploadDocument(formDataDoc);
            toast.success('Document uploaded successfully!');
            setSelectedFile(null);
            fetchDocuments();
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.error('Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadDocument = async (doc) => {
        try {
            const blob = await documentsAPI.downloadDocument(doc.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.name || 'document';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Document downloaded');
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            await documentsAPI.deleteDocument(docId);
            toast.success('Document deleted successfully!');
            fetchDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Failed to delete document');
        }
    };

    const loadContact = async () => {
        setLoading(true);
        try {
            const response = await getContact(id);
            const data = splitLegacyName(response.data);
            // Nulls from the API would make these inputs uncontrolled.
            const first = data.first_name || '';
            const last = data.last_name || '';
            setFormData({
                ...data,
                first_name: first,
                last_name: last,
                preferred_name: data.preferred_name || '',
                email: data.email || '',
            });
            setNameBaseline({ first, last, name: data.name || '' });
        } catch (error) {
            console.error('Error loading contact:', error);
            toast.error('Failed to load contact');
            navigate('/contacts');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const first = (formData.first_name || '').trim();
        const last = (formData.last_name || '').trim();
        if (!first && !last) {
            toast.error('Please enter a first name');
            return;
        }
        const nameUntouched = nameBaseline
            && nameBaseline.name
            && nameBaseline.first === first
            && nameBaseline.last === last;
        const fullName = nameUntouched ? nameBaseline.name : buildFullName(first, last);

        setLoading(true);

        // Send null (not "") for an unset birthday — the API's DateField rejects
        // empty strings. Same for any other blank optional fields.
        const payload = {
            ...formData,
            // `name` is the full legal name the rest of the CRM reads, kept in
            // sync with the first/last boxes the agent actually fills in.
            name: fullName,
            birthday: formData.birthday || null,
            // Email is optional — send null so the API doesn't trip the unique
            // index on a second email-less contact.
            email: (formData.email || '').trim() || null,
        };

        try {
            if (isEdit) {
                await updateContact(id, payload);
                // Invalidate contacts cache to ensure fresh data
                apiCache.invalidatePattern('contacts-');
                toast.success('Contact updated successfully!');
            } else {
                await createContact(payload);
                // Invalidate contacts cache to ensure fresh data
                apiCache.invalidatePattern('contacts-');
                toast.success('Contact created successfully!');
            }
            navigate('/contacts');
        } catch (error) {
            console.error('Error saving contact:', error);
            toast.error(getServerErrorMessage(error, isEdit ? 'Failed to update contact' : 'Failed to create contact'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData({
                ...formData,
                tags: [...formData.tags, tagInput.trim()]
            });
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData({
            ...formData,
            tags: formData.tags.filter(tag => tag !== tagToRemove)
        });
    };

    if (loading && isEdit) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white p-3 sm:p-4 md:p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                    <button
                        onClick={() => navigate('/contacts')}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Contacts
                    </button>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {isEdit ? 'Edit Contact' : 'Add New Contact'}
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">
                        {isEdit ? 'Update contact information' : 'Add a new contact to your database'}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow">
                    {/* Basic Information */}
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                            <User className="w-5 h-5 mr-2" />
                            Basic Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    required
                                    placeholder="Jing"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    placeholder="Xiao"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Preferred Name <span className="text-xs text-gray-500">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.preferred_name}
                                    onChange={(e) => setFormData({ ...formData, preferred_name: e.target.value })}
                                    placeholder="Andrew"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    The name they go by, if it differs from their legal name.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company <span className="text-xs text-gray-500">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                    placeholder="ABC Corporation"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Position <span className="text-xs text-gray-500">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.position}
                                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                    placeholder="Sales Manager"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email <span className="text-xs text-gray-500">(optional)</span>
                                </label>
                                <input
                                    type="email"
                                    value={formData.email || ''}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="john@example.com"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="0400 000 000"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Alternate Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.alternate_phone}
                                    onChange={(e) => setFormData({ ...formData, alternate_phone: e.target.value })}
                                    placeholder="0400 000 000"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Type
                                </label>
                                <select
                                    value={formData.contact_type}
                                    onChange={(e) => setFormData({ ...formData, contact_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                >
                                    <option value="general">General</option>
                                    <option value="buyer">Buyer</option>
                                    <option value="seller">Seller</option>
                                    <option value="tenant">Tenant</option>
                                    <option value="landlord">Landlord</option>
                                    <option value="investor">Investor</option>
                                    <option value="buyer_advocate">Buyers Advocate</option>
                                </select>
                            </div>

                            {isAdmin && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Assigned Agent
                                    </label>
                                    <select
                                        value={formData.agent || ''}
                                        onChange={(e) => setFormData({ ...formData, agent: e.target.value ? Number(e.target.value) : null })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                    >
                                        <option value="">Unassigned</option>
                                        {agents.map((a) => (
                                            <option key={a.id} value={a.id}>{a.name || a.email}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Birthday
                                </label>
                                <input
                                    type="date"
                                    value={formData.birthday || ''}
                                    onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Address Information */}
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                            <MapPin className="w-5 h-5 mr-2" />
                            Address
                        </h2>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Street Address
                            </label>
                            <input
                                type="text"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder="123 Main Street"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                            />
                        </div>
                    </div>

                    {/* Additional Details */}
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                            <FileText className="w-5 h-5 mr-2" />
                            Additional Details
                        </h2>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Source
                                    </label>
                                    <select
                                        value={formData.source}
                                        onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                    >
                                        {/* Auto-assigned "signed up via" sources. Shown (not in the
                                            manual list) so editing a portal contact doesn't blank or
                                            overwrite where they actually came from. */}
                                        {SYSTEM_SOURCE_LABELS[formData.source] && (
                                            <option value={formData.source}>
                                                {SYSTEM_SOURCE_LABELS[formData.source]}
                                            </option>
                                        )}
                                        <option value="website">Website</option>
                                        <option value="referral">Referral</option>
                                        <option value="ofi">Open For Inspection</option>
                                        <option value="social_media">Social Media</option>
                                        <option value="cold_call">Cold Call</option>
                                        <option value="walk_in">Walk In</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Preferred Contact Method
                                    </label>
                                    <select
                                        value={formData.preferred_contact_method}
                                        onChange={(e) => setFormData({ ...formData, preferred_contact_method: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                    >
                                        <option value="email">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="sms">SMS</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <Tag className="w-4 h-4 mr-1" />
                                    Tags
                                </label>
                                <div className="flex items-center space-x-2 mb-2">
                                    <input
                                        type="text"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                        placeholder="Add a tag..."
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddTag}
                                        className="px-4 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be]"
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {formData.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#55C5D0] bg-opacity-10 text-[#55C5D0]"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                                className="ml-2 text-[#55C5D0] hover:text-[#3bb3be]"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows="4"
                                    placeholder="Any additional notes about this contact..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Documents Section */}
                    {
                        isEdit && id && (
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <FileText className="w-5 h-5 mr-2" />
                                    Documents
                                </h2>

                                {/* Upload Section */}
                                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="file"
                                            onChange={handleFileSelect}
                                            className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#55C5D0] file:text-white hover:file:bg-[#45b5c0]"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleUploadDocument}
                                            disabled={!selectedFile || uploading}
                                            className="px-4 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            <Upload className="w-4 h-4" />
                                            {uploading ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                </div>

                                {/* Documents List */}
                                {loadingDocs ? (
                                    <div className="text-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0] mx-auto"></div>
                                    </div>
                                ) : documents.length > 0 ? (
                                    <div className="space-y-2">
                                        {documents.map((doc) => (
                                            <div
                                                key={doc.id}
                                                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:shadow-sm transition-shadow"
                                            >
                                                <div className="flex items-center gap-3 flex-1">
                                                    <FileText className="w-5 h-5 text-gray-500" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : 'Unknown size'} •
                                                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Unknown date'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadDocument(doc)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                                        title="Download"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteDocument(doc.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">No documents uploaded yet</p>
                                )}
                            </div>
                        )
                    }

                    {/* Preferences */}
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            Communication Preferences
                        </h2>
                        <div className="flex flex-wrap gap-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.email_opt_in}
                                    onChange={(e) => setFormData({ ...formData, email_opt_in: e.target.checked })}
                                    className="w-4 h-4 text-[#0087FF] border-gray-300 rounded focus:ring-[#0087FF]"
                                />
                                <span className="ml-2 text-sm text-gray-700">Email opt-in</span>
                            </label>

                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.sms_opt_in}
                                    onChange={(e) => setFormData({ ...formData, sms_opt_in: e.target.checked })}
                                    className="w-4 h-4 text-[#0087FF] border-gray-300 rounded focus:ring-[#0087FF]"
                                />
                                <span className="ml-2 text-sm text-gray-700">SMS opt-in</span>
                            </label>

                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.do_not_contact}
                                    onChange={(e) => setFormData({ ...formData, do_not_contact: e.target.checked })}
                                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-600"
                                />
                                <span className="ml-2 text-sm text-red-700 font-medium">Do not contact</span>
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="p-4 sm:p-6 bg-gray-50 flex flex-col sm:flex-row justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/contacts')}
                            className="w-full sm:w-auto px-4 sm:px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#55C5D0] text-sm sm:text-base"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] focus:outline-none focus:ring-2 focus:ring-[#55C5D0] disabled:opacity-50 text-sm sm:text-base"
                        >
                            {loading ? 'Saving...' : (isEdit ? 'Update Contact' : 'Create Contact')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactFormComponent;

